import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { es, enUS } from 'date-fns/locale';
import { format } from 'date-fns';
import { useBoardGroups } from '@/features/boards/public';
import { useOpenTask } from '../../hooks/useOpenTask';
import { useProjectStatuses } from '../../hooks/useProjectStatuses';
import { useTimelineScroll } from '../../hooks/useTimelineScroll';
import { TimelineWeekHeader } from './TimelineWeekHeader';
import { TimelineRows } from './TimelineRows';
import { TimelineLoading } from './TimelineLoading';
import { useTimelineTasks } from '../../hooks/useTimelineTasks';
import { useTimelineData } from '../../hooks/useTimelineData';
import { groupConsecutiveAbsences } from '../../utils/timeline-absence-grouping';
import { buildPersonRows } from '../../utils/timeline-row-builder';
import {
  buildGroupedTimelineRows,
  toTimelineGroupBy,
  resolveSectionOrder,
} from '../../utils/timeline-group-builder';
import {
  computeColumns,
  computeMarkerOffset,
  getDefaultTimelineWindowStart,
  shiftWindowStart,
  TIMELINE_PERSON_COLUMN_WIDTH_PX,
  TIMELINE_ZOOM_CONFIG,
} from '../../utils/timeline-geometry';
import type { GanttGroupLabels } from '../../adapters';
import type { GroupByOption, TaskFilters, TimelineZoom } from '../../types';

interface TaskTimelineViewProps {
  projectId: string;
  boardId: string;
  filters: TaskFilters;
  showCompleted: boolean;
  /** Shared with List/Board — the same "Agrupar por" state, driven by the `ActionToolbar` above this view. */
  groupBy: GroupByOption;
  /** "Lapso" granularity, owned by the toolbar (see `TimelineViewControls`) and persisted per board. */
  zoom: TimelineZoom;
  /** Incremented by the toolbar's "Hoy" button; each change re-centers the grid on today. */
  scrollToTodayNonce: number;
}

export function TaskTimelineView({
  projectId,
  boardId,
  filters,
  showCompleted,
  groupBy,
  zoom,
  scrollToTodayNonce,
}: TaskTimelineViewProps) {
  const { t, i18n } = useTranslation('tasks');
  const locale = i18n.language.startsWith('es') ? es : enUS;
  const openTask = useOpenTask();

  // The rendered range grows on demand as the user scrolls (see `useTimelineScroll`),
  // so these track the *current* extent rather than a fixed preloaded window.
  const [windowStart, setWindowStart] = useState<Date>(() => getDefaultTimelineWindowStart(zoom));
  const [bufferUnits, setBufferUnits] = useState<number>(
    () => TIMELINE_ZOOM_CONFIG[zoom].initialBufferUnits,
  );

  // Switching zoom rebuilds the range from scratch — reset during render rather
  // than in an effect, so no frame is ever painted with the stale range.
  const [zoomBaseline, setZoomBaseline] = useState<TimelineZoom>(zoom);
  if (zoom !== zoomBaseline) {
    setZoomBaseline(zoom);
    setWindowStart(getDefaultTimelineWindowStart(zoom));
    setBufferUnits(TIMELINE_ZOOM_CONFIG[zoom].initialBufferUnits);
  }

  const timelineGroupBy = toTimelineGroupBy(groupBy);
  const { statuses } = useProjectStatuses(boardId);
  const { groups: boardGroups } = useBoardGroups(boardId);

  const { columnWidthPx, extendUnits } = TIMELINE_ZOOM_CONFIG[zoom];
  const columns = computeColumns(windowStart, zoom, bufferUnits, locale);
  const windowEnd = columns[columns.length - 1].endDate;
  const totalWidthPx = columns.length * columnWidthPx;

  const {
    tasks,
    isLoading: tasksLoading,
    isError: tasksError,
  } = useTimelineTasks({ projectId, boardId, filters, showCompleted });

  const assigneeIds = Array.from(
    new Set(tasks.map((task) => task.assignee?.id).filter((id): id is string => !!id)),
  );

  const {
    milestones,
    absences,
    isLoading: dataLoading,
    isError: dataError,
  } = useTimelineData({
    projectId,
    boardId,
    dateStart: format(windowStart, 'yyyy-MM-dd'),
    dateEnd: format(windowEnd, 'yyyy-MM-dd'),
    userIds: assigneeIds,
  });

  const labels: GanttGroupLabels = {
    unassigned: t('gantt.unassigned'),
    noGroup: t('gantt.noGroup'),
    priority: {
      urgent: t('priority.urgent'),
      high: t('priority.high'),
      medium: t('priority.medium'),
      low: t('priority.low'),
      none: t('priority.none'),
    },
  };

  const rows = timelineGroupBy
    ? []
    : buildPersonRows(tasks, milestones, groupConsecutiveAbsences(absences));
  const sections = timelineGroupBy
    ? buildGroupedTimelineRows(
        tasks,
        timelineGroupBy,
        labels,
        resolveSectionOrder(timelineGroupBy, statuses, boardGroups),
      )
    : [];

  const isLoading = tasksLoading || dataLoading;
  const isError = tasksError || dataError;
  const isEmpty =
    !isLoading && !isError && (timelineGroupBy ? sections.length === 0 : rows.length === 0);
  /** Mirrors the render branch below: only in this state is the scrollable grid actually in the DOM. */
  const isGridMounted = !isError && !isLoading && !isEmpty;
  const todayOffsetPx = computeMarkerOffset(new Date(), windowStart, windowEnd, totalWidthPx);
  const personColumnLabel = t(
    timelineGroupBy ? 'timeline.columns.responsible' : 'timeline.columns.person',
  );

  const { scrollRef, scrollToToday, handleScroll } = useTimelineScroll({
    zoom,
    windowStart,
    windowEnd,
    totalWidthPx,
    isGridMounted,
    onExtendPast: () => {
      setWindowStart((prev) => shiftWindowStart(prev, zoom, -extendUnits));
      setBufferUnits((prev) => prev + extendUnits);
      return extendUnits * columnWidthPx;
    },
    onExtendFuture: () => setBufferUnits((prev) => prev + extendUnits),
  });

  useEffect(() => {
    if (scrollToTodayNonce > 0) scrollToToday('smooth');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires only when the toolbar's "Hoy" button bumps the nonce
  }, [scrollToTodayNonce]);

  return (
    <div data-testid="tasks-timeline-view" className="flex h-full flex-col">
      <div ref={scrollRef} onScroll={handleScroll} className="min-h-0 flex-1 overflow-auto">
        {isError ? (
          <p
            data-testid="tasks-timeline-error-message"
            className="flex h-full items-center justify-center p-8 text-sm text-muted-foreground"
          >
            {t('timeline.error')}
          </p>
        ) : isLoading ? (
          <TimelineLoading />
        ) : isEmpty ? (
          <div
            data-testid="tasks-timeline-empty"
            className="flex h-full flex-col items-center justify-center gap-1 p-8 text-center"
          >
            <p className="text-sm font-medium text-foreground">{t('timeline.empty.title')}</p>
            <p className="text-sm text-muted-foreground">{t('timeline.empty.description')}</p>
          </div>
        ) : (
          <div className="relative min-w-max">
            <TimelineWeekHeader
              columns={columns}
              columnWidthPx={columnWidthPx}
              personColumnLabel={personColumnLabel}
            />
            {todayOffsetPx != null && (
              <div
                data-testid="tasks-timeline-today-line"
                className="pointer-events-none absolute inset-y-0 w-px bg-primary/60"
                style={{ left: TIMELINE_PERSON_COLUMN_WIDTH_PX + todayOffsetPx }}
              />
            )}
            <TimelineRows
              timelineGroupBy={timelineGroupBy}
              sections={sections}
              rows={rows}
              columnCount={columns.length}
              columnWidthPx={columnWidthPx}
              windowStart={windowStart}
              windowEnd={windowEnd}
              onTaskClick={openTask}
            />
          </div>
        )}
      </div>
    </div>
  );
}
